/** Fusionne les réponses GET /capitalflow/strategies vers allS / allComp / stratInv / investors. */

import { STATIC_INVESTORS } from "./strategiesStaticEnrich";

function toNum(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function normalizeDbSignal(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).toUpperCase();
  if (s === "BUY" || s.includes("ACCUMULATE")) return "BUY";
  if (s === "HOLD") return "HOLD";
  return "WATCH";
}

function toMosPct(v) {
  let m = toNum(v);
  if (m == null) return null;
  if (Math.abs(m) < 2.5) m = m * 100;
  if (Math.abs(m) > 200) return null;
  return m;
}

/** Mappe les colonnes SQL/API vers les champs attendus par IndexStrategiesPage. */
export function parseComp(c) {
  const sigRaw = c.dd_signal ?? c.signal ?? c._sig ?? null;
  return {
    ...c,
    region: c.region ?? c.Region ?? "",
    country: c.country ?? c.market ?? "",
    _is: toNum(c.iscore ?? c._is),
    _fn: toNum(c.fund_score ?? c._fn),
    _ti: toNum(c.trend_score ?? c._ti),
    _mo: toMosPct(c.mos_pct ?? c._mo),
    _sig: normalizeDbSignal(sigRaw),
  };
}

function initialsFromName(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

/** Clé investisseur tolérante (James O Shaughnessy vs James O'Shaughnessy). */
function investorEnrichKey(name, staticEnrich) {
  if (!name || !staticEnrich) return null;
  if (staticEnrich[name]) return name;
  const norm = (s) =>
    String(s)
      .trim()
      .toLowerCase()
      .replace(/[''`]/g, "")
      .replace(/\s+/g, " ");
  const target = norm(name);
  for (const key of Object.keys(staticEnrich)) {
    if (norm(key) === target) return key;
  }
  return null;
}

function staticEnrichFor(name, staticEnrich) {
  const key = investorEnrichKey(name, staticEnrich);
  return key ? staticEnrich[key] : null;
}

/** Étiquette courte (Value, Quantitative…) vs texte descriptif long. */
export function isPhilosophyStyleLabel(value) {
  if (value == null || value === "") return false;
  const t = String(value).trim();
  if (t.length > 36) return false;
  const words = t.split(/\s+/);
  if (words.length > 4) return false;
  return true;
}

function normalizeInvestorEntry(inv, staticEnrich) {
  const enrich = staticEnrichFor(inv.name, staticEnrich);
  let style = inv.style || enrich?.style || null;
  let philosophy = inv.philosophy || enrich?.philosophy || "";

  if (style && !isPhilosophyStyleLabel(style)) {
    if (!philosophy) philosophy = style;
    style = enrich?.style || null;
  }
  if (!style && enrich?.style) style = enrich.style;

  return {
    ini: inv.ini || enrich?.ini || initialsFromName(inv.name),
    style,
    bio: inv.bio || enrich?.bio || "",
    philosophy,
  };
}

/** Investisseurs dérivés de stratInv + liste API (sans dépendre de INVESTORS statique). */
export function buildInvestorsMap(api, staticEnrich = {}) {
  const map = {};
  (api.investors || []).forEach((inv) => {
    if (!inv?.name) return;
    map[inv.name] = normalizeInvestorEntry(inv, staticEnrich);
  });

  const stratInv = api.stratInv || {};
  Object.values(stratInv).forEach((name) => {
    if (!name || map[name]) return;
    const enrich = staticEnrichFor(name, staticEnrich);
    map[name] = enrich
      ? normalizeInvestorEntry({ name, ...enrich }, staticEnrich)
      : {
          ini: initialsFromName(name),
          style: null,
          bio: "",
          philosophy: "",
        };
  });

  (api.strategies || []).forEach((s) => {
    const name = s.investor;
    if (!name || map[name]) return;
    const enrich = staticEnrichFor(name, staticEnrich);
    map[name] = enrich
      ? normalizeInvestorEntry({ name, ...enrich }, staticEnrich)
      : {
          ini: initialsFromName(name),
          style: null,
          bio: "",
          philosophy: "",
        };
  });

  return map;
}

export function buildStratInvFromApi(api) {
  const out = { ...(api.stratInv || {}) };
  (api.links || []).forEach((l) => {
    if (l.strategy && l.investor) out[l.strategy] = l.investor;
  });
  (api.strategies || []).forEach((s) => {
    if (s.name && s.investor) out[s.name] = s.investor;
  });
  return out;
}

export function mergeStrategiesPagePayload(prev, api) {
  if (!api || api.error) return prev;
  const out = {
    allS: prev?.allS ? [...prev.allS] : [],
    allComp: prev?.allComp ? [...prev.allComp] : [],
    stratInv: { ...(prev?.stratInv || {}) },
    investorsMap: { ...(prev?.investorsMap || {}) },
  };

  const byKey = new Map();
  out.allS.forEach((s) => {
    byKey.set(`${s.name}|${s.analYear}`, s);
  });
  (api.strategies || []).forEach((s) => {
    byKey.set(`${s.name}|${s.analYear}`, s);
  });
  out.allS = Array.from(byKey.values());

  out.stratInv = buildStratInvFromApi({
    stratInv: { ...out.stratInv, ...(api.stratInv || {}) },
    links: api.links,
    strategies: api.strategies,
  });

  const compKey = (c) => `${c.strategy}|${c.symbol}`;
  const compMap = new Map();
  out.allComp.forEach((c) => compMap.set(compKey(c), c));
  (api.constituents || []).forEach((c) => compMap.set(compKey(c), parseComp(c)));
  out.allComp = Array.from(compMap.values());

  out.investorsMap = buildInvestorsMap(
    { ...api, stratInv: out.stratInv },
    { ...STATIC_INVESTORS, ...(prev?.investorsMap || {}) }
  );

  return out;
}

export function buildInvestorOverrides(api) {
  const overrides = {};
  (api.investors || []).forEach((inv) => {
    if (!inv.name) return;
    const cur = overrides[inv.name] || {};
    overrides[inv.name] = {
      ...cur,
      ...(inv.style ? { style: inv.style } : {}),
      ...(inv.bio ? { bio: inv.bio } : {}),
      ...(inv.philosophy ? { philosophy: inv.philosophy } : {}),
      ...(inv.ini ? { ini: inv.ini } : {}),
    };
  });
  return overrides;
}
